import { useTranslation } from "react-i18next";
import { History } from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/v2/spinner";

import { useChat } from "../context/use-chat";
import { hasRealMessages } from "../types";
import { useAutoScroll } from "../hooks/use-auto-scroll";

import { EmptyState } from "./empty-state";
import { MessageBubble } from "./message-bubble";
import { ScrollToBottomButton } from "./scroll-to-bottom-button";
import { TypingIndicator } from "./typing-indicator";

/** Placeholder rows while a saved chat opens: a question, then a reply. */
const LoadingRows = () => (
  <div aria-hidden="true" className="space-y-4">
    {[0, 1].map((i) => (
      <div key={i} className="space-y-4">
        <Skeleton className="ms-auto h-8 w-2/3 rounded-2xl" />
        <div className="flex items-start gap-2.5">
          <Skeleton className="size-6 shrink-0 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-11/12" />
            <Skeleton className="h-3 w-4/5" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

export const MessageList = () => {
  const { t } = useTranslation(["common"]);
  const {
    messages,
    isTyping,
    retryLast,
    sessionId,
    hasEarlier,
    loadingConversation,
    loadingEarlier,
    loadEarlier,
    pendingReplyId,
    editMessage,
    regenerate,
    temporary,
    highlightId,
    clearHighlight,
  } = useChat();
  // Actions that start a new reply wait while one is streaming or pending.
  const busy = isTyping || pendingReplyId !== null;
  const isEmpty = !hasRealMessages(messages);

  // The welcome screen reads top-down (and can be taller than the panel);
  // only a conversation follows its newest message.
  const { scrollRef, isPinned, scrollToBottom } = useAutoScroll(
    [messages, isTyping],
    { enabled: !isEmpty },
  );

  // Stable across renders so memoised bubbles (and their parsed markdown)
  // are not redone on every status update; reads the pin state via a ref.
  const isPinnedRef = useRef(isPinned);
  useLayoutEffect(() => {
    isPinnedRef.current = isPinned;
  }, [isPinned]);
  const handleChartRender = useCallback(() => {
    if (isPinnedRef.current) scrollToBottom("instant");
  }, [scrollToBottom]);

  // Opening another chat lands on its latest turn; the welcome screen starts
  // at its top.
  useLayoutEffect(() => {
    if (isEmpty) scrollRef.current?.scrollTo({ top: 0 });
    else scrollToBottom("instant");
  }, [sessionId, isEmpty, scrollRef, scrollToBottom]);

  // A turn picked from the chat list: bring it to the middle of the view and
  // pulse it once (runs after the jump-to-latest above).
  useEffect(() => {
    if (!highlightId) return;
    const el = scrollRef.current?.querySelector<HTMLElement>(
      `[data-message-id="${CSS.escape(highlightId)}"]`,
    );
    // Not in the chat (e.g. outside what's loaded): nothing to pulse.
    if (!el) {
      clearHighlight();
      return;
    }
    el.scrollIntoView({ block: "center" });
    const timer = window.setTimeout(clearHighlight, 1800);
    return () => window.clearTimeout(timer);
  }, [highlightId, messages, scrollRef, clearHighlight]);

  // Older turns go in above what's on screen; keep what the user was reading
  // where it was instead of letting it jump down by the added height. The
  // anchor applies only when the first message actually changed (older turns
  // arrived) — a reply landing mid-fetch must not consume it — and is dropped
  // if the fetch ends without prepending anything.
  const firstId = messages.find((m) => !m.i18nKey)?.id;
  const anchorRef = useRef<{
    height: number;
    top: number;
    firstId?: string;
  } | null>(null);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    const anchor = anchorRef.current;
    if (!el || !anchor || anchor.firstId === firstId) return;
    anchorRef.current = null;
    el.scrollTop = anchor.top + (el.scrollHeight - anchor.height);
  }, [firstId, scrollRef]);
  useEffect(() => {
    if (!loadingEarlier) anchorRef.current = null;
  }, [loadingEarlier]);

  // The button stays enabled (and focused) while loading — `loadEarlier`
  // ignores repeat presses. When the last page arrives it goes away; focus
  // then moves to the log rather than dropping to <body>.
  const loadEarlierFocusedRef = useRef(false);
  const handleLoadEarlier = (e: { currentTarget: HTMLElement }) => {
    const el = scrollRef.current;
    if (el) {
      anchorRef.current = {
        height: el.scrollHeight,
        top: el.scrollTop,
        firstId,
      };
    }
    loadEarlierFocusedRef.current = document.activeElement === e.currentTarget;
    void loadEarlier();
  };
  useEffect(() => {
    if (hasEarlier || !loadEarlierFocusedRef.current) return;
    loadEarlierFocusedRef.current = false;
    scrollRef.current?.focus({ preventScroll: true });
  }, [hasEarlier, scrollRef]);

  // History arriving in bulk (a chat opened, older turns loaded) must not be
  // read out turn by turn: the log stops announcing while it lands, and for
  // one frame after, then resumes for new replies.
  const bulkLoading = loadingConversation || loadingEarlier;
  const [quiet, setQuiet] = useState(false);
  useEffect(() => {
    if (bulkLoading) {
      setQuiet(true);
      return;
    }
    const frame = requestAnimationFrame(() => setQuiet(false));
    return () => cancelAnimationFrame(frame);
  }, [bulkLoading]);

  return (
    <div className="relative flex-1 overflow-hidden">
      <div
        ref={scrollRef}
        role="log"
        // Focusable from script (not a tab stop) so focus has somewhere to
        // land when the control that held it goes away.
        tabIndex={-1}
        aria-live={quiet ? "off" : "polite"}
        aria-relevant="additions"
        aria-busy={loadingConversation}
        // Transparent over the sidebar canvas; the edges fade so messages
        // dissolve under the header and composer instead of being cut off.
        className="maya-fade-edges absolute inset-0 space-y-4 overflow-y-auto px-4 py-4 scrollbar-thin focus-visible:outline-hidden"
      >
        {loadingConversation ? (
          <LoadingRows />
        ) : isEmpty ? (
          <EmptyState />
        ) : (
          <>
            {hasEarlier && (
              <div className="flex justify-center">
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  onClick={handleLoadEarlier}
                  aria-busy={loadingEarlier}
                  className="rounded-full text-muted-foreground"
                >
                  {loadingEarlier ? (
                    <Spinner size="sm" />
                  ) : (
                    <History className="size-3.5" />
                  )}
                  {t("common:maya.load_earlier")}
                </Button>
              </div>
            )}
            {messages.map((m) => (
              <MessageBubble
                key={m.id}
                message={m}
                onRetry={m.status === "failed" ? retryLast : undefined}
                onChartRender={handleChartRender}
                busy={busy}
                pending={m.id === pendingReplyId}
                highlighted={m.id === highlightId}
                // Editing works on saved messages only.
                onEdit={temporary ? undefined : editMessage}
                onRegenerate={regenerate}
              />
            ))}
          </>
        )}
        {/* A regenerating reply shows its "thinking" in place instead. */}
        {isTyping && !pendingReplyId && <TypingIndicator />}
      </div>
      <ScrollToBottomButton
        visible={!isPinned && !isEmpty && !loadingConversation}
        onClick={() => scrollToBottom("smooth")}
      />
    </div>
  );
};
